import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  StocksState,
  Stock,
  Ticker,
  FilterState,
  SortConfig,
  SortColumn,
  SortDirection,
  MarketData,
} from "@/types/stock.types";
import {
  stocksApi,
  StockFilterRequest,
} from "@/services/api/endpoints/stocks.api";

// Initial state
const initialState: StocksState = {
  topGainers: [],
  selectedStock: null,
  searchResults: [],
  realtimeData: {},
  filters: {
    price: "any",
    changePercent: "any",
    exchanges: {
      all: true,
      nyse: true,
      otc: true,
      nasdaq: true,
      amex: true,
    },
    currentVolume: "any",
    relativeVolume: "any",
    float: "any",
    watchlistedSymbols: [],
  },
  sortConfig: {
    column: "change",
    direction: "desc",
  },
  loading: false,
  error: null,
  lastSync: null,
};

// Async thunks
export const fetchTopGainers = createAsyncThunk(
  "stocks/fetchTopGainers",
  async (filters: StockFilterRequest | undefined, { rejectWithValue }) => {
    try {
      const stocks = await stocksApi.filterStocks(filters);
      return stocks;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to fetch top gainers");
    }
  },
);

export const filterStocks = createAsyncThunk(
  "stocks/filter",
  async (filters: StockFilterRequest, { rejectWithValue }) => {
    try {
      const stocks = await stocksApi.filterStocks(filters);
      return stocks;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to filter stocks");
    }
  },
);

export const fetchStockDetails = createAsyncThunk(
  "stocks/fetchStockDetails",
  async (symbol: string, { rejectWithValue }) => {
    try {
      const stock = await stocksApi.getStock(symbol);
      return stock;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to fetch stock details");
    }
  },
);

// Slice
const stocksSlice = createSlice({
  name: "stocks",
  initialState,
  reducers: {
    setSelectedStock: (state, action: PayloadAction<Stock | null>) => {
      state.selectedStock = action.payload;
    },
    updateFilters: (state, action: PayloadAction<Partial<FilterState>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setSortConfig: (state, action: PayloadAction<SortConfig>) => {
      state.sortConfig = action.payload;
    },
    updateRealtimeData: (state, action: PayloadAction<MarketData>) => {
      const { symbol, price, timestamp, volume } = action.payload;
      state.realtimeData[symbol] = { symbol, price, timestamp, volume };

      // Update the stock in topGainers if it exists
      const stockIndex = state.topGainers.findIndex(
        (stock) => stock.ticker === symbol,
      );
      if (stockIndex !== -1) {
        state.topGainers[stockIndex].price = price;
      }

      // Update selected stock if it matches
      if (state.selectedStock?.ticker === symbol) {
        state.selectedStock.price = price;
      }
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Top Gainers
    builder
      .addCase(fetchTopGainers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTopGainers.fulfilled, (state, action) => {
        state.loading = false;
        state.topGainers = action.payload;
        state.lastSync = Date.now();
        state.error = null;
      })
      .addCase(fetchTopGainers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch Stock Details
    builder
      .addCase(fetchStockDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStockDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedStock = action.payload;
        state.error = null;
      })
      .addCase(fetchStockDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

// Actions
export const {
  setSelectedStock,
  updateFilters,
  setSortConfig,
  updateRealtimeData,
  clearError,
} = stocksSlice.actions;

// Selectors
export const selectTopGainers = (state: { stocks: StocksState }) =>
  state.stocks.topGainers;
export const selectSelectedStock = (state: { stocks: StocksState }) =>
  state.stocks.selectedStock;
export const selectFilters = (state: { stocks: StocksState }) =>
  state.stocks.filters;
export const selectSortConfig = (state: { stocks: StocksState }) =>
  state.stocks.sortConfig;
export const selectStocksLoading = (state: { stocks: StocksState }) =>
  state.stocks.loading;
export const selectStocksError = (state: { stocks: StocksState }) =>
  state.stocks.error;
export const selectLastSync = (state: { stocks: StocksState }) =>
  state.stocks.lastSync;
export const selectRealtimeData = (state: { stocks: StocksState }) =>
  state.stocks.realtimeData;

// Derived selectors with memoization would go here (using reselect if needed)
export const selectFilteredAndSortedStocks = (state: {
  stocks: StocksState;
}) => {
  const { topGainers, sortConfig } = state.stocks;

  // Apply sorting
  const sorted = [...topGainers].sort((a, b) => {
    let aVal: number | string;
    let bVal: number | string;

    switch (sortConfig.column) {
      case "change":
        aVal = a.change;
        bVal = b.change;
        break;
      case "symbol":
        aVal = a.ticker;
        bVal = b.ticker;
        break;
      case "price":
        aVal = a.price;
        bVal = b.price;
        break;
      case "float":
        aVal =
          parseFloat(a.float.replace(/[KM]/g, "")) *
          (a.float.includes("M") ? 1000 : 1);
        bVal =
          parseFloat(b.float.replace(/[KM]/g, "")) *
          (b.float.includes("M") ? 1000 : 1);
        break;
      case "volume":
        aVal =
          parseFloat(a.volume.replace(/[KM]/g, "")) *
          (a.volume.includes("M") ? 1000 : 1);
        bVal =
          parseFloat(b.volume.replace(/[KM]/g, "")) *
          (b.volume.includes("M") ? 1000 : 1);
        break;
      case "relVol90":
        aVal = a.relativeVolumeFor50Days;
        bVal = b.relativeVolumeFor50Days;
        break;
      case "marketCap":
        aVal =
          parseFloat(a.marketCap.replace(/[KM]/g, "")) *
          (a.marketCap.includes("M") ? 1000 : 1);
        bVal =
          parseFloat(b.marketCap.replace(/[KM]/g, "")) *
          (b.marketCap.includes("M") ? 1000 : 1);
        break;
      default:
        aVal = 0;
        bVal = 0;
    }

    if (typeof aVal === "string" && typeof bVal === "string") {
      return sortConfig.direction === "asc"
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }
    return sortConfig.direction === "asc"
      ? (aVal as number) - (bVal as number)
      : (bVal as number) - (aVal as number);
  });

  return sorted;
};

export default stocksSlice.reducer;
